import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _differenceWith from "lodash/differenceWith";
var _class;
/*
  A mapping collection is a list of models that associate assets with an asset tag value.
*/
import { action, computed } from 'mobx';
import { AssetType } from '@kentik/ui-shared/labels/lib';
import $assetTags from 'app/stores/assetTags/$assetTags';
import { Collection } from 'core/model/Collection';
import AssetTagValueMappingModel from './AssetTagValueMappingModel';
let AssetTagValueMappingCollection = (_class = class AssetTagValueMappingCollection extends Collection {
  constructor(data = [], {
    valueModel
  }) {
    super(data);
    // keep the value associated with the mappings - together they make up a complete asset tag value in the db
    this.valueModel = void 0;
    this.valueModel = valueModel;
  }
  get model() {
    return AssetTagValueMappingModel;
  }
  getFilterValues(model) {
    return super.getFilterValues(model).concat(model.name);
  }

  /**
   * Returns a unique list of all asset types represented in the mappings
   */
  get assetTypes() {
    return Array.from(this.get().reduce((acc, model) => acc.add(model.get('asset_type')), new Set()));
  }

  /**
   * Checks all the asset types in the mappings for their fetched flag
   */
  get assetTypesHaveFetched() {
    return this.assetTypes.every(assetType => !!$assetTags.getAssetCollection(assetType)?.collection?.hasFetched);
  }

  /**
   * Breaks down usage by asset type
   */
  get usage() {
    return this.get().reduce((acc, model) => {
      const {
        asset_type
      } = model.get() || {};
      if (!acc[asset_type]) {
        acc[asset_type] = 0;
      }
      acc[asset_type] += 1;
      return acc;
    }, {});
  }

  /**
   * Returns the asset type with the highest usage count, falling back to device as the default
   */
  get defaultAssetType() {
    const usageItems = Object.entries(this.usage);
    if (usageItems.length === 0) {
      return AssetType.DEVICE;
    }
    return usageItems.reduce((max, current) => current[1] > max[1] ? current : max)[0];
  }

  /**
   * Groups selects by asset type.
   * Used for loading selections into each asset type's own collection.
   */
  get selectionsByAssetType() {
    return this.get().reduce((acc, model) => {
      const assetType = model.get('asset_type');
      if (!acc[assetType]) {
        acc[assetType] = [];
      }
      acc[assetType].push(model.get('asset_id'));
      return acc;
    }, {});
  }

  /**
   * Initializes selections into each asset type's own collection.
   * This basically sets up selections in the mapping dialog table.
   */
  initAssetSelections() {
    const {
      selectionsByAssetType
    } = this;

    // ensure we're working with a blank slate
    this.resetAssetCollections();
    Object.entries(selectionsByAssetType).forEach(([assetType, selections]) => {
      const {
        collection
      } = $assetTags.getAssetCollection(assetType);
      if (collection && selections.length > 0) {
        const selectableModels = selections.map(id => collection.get(id)).filter(model => model && !!model.isSelectable);
        collection.selected = selectableModels;
      }
    });
  }

  /**
   * Replaces the mappings with the selections from each asset type's own collection.
   * Mappings that are already defined with a different value are filtered out.
   */
  setMappingsFromAssetSelections() {
    // assemble a complete list of selections across all asset types
    const nextSelections = Object.values(AssetType).flatMap(assetType => {
      const {
        collection
      } = $assetTags.getAssetCollection(assetType);
      return collection?.selectedArray.map(model => ({
        asset_type: assetType,
        asset_id: model.id
      })).filter(item => !this.valueModel.disabledMappings.has(`${assetType}:${item.asset_id}`)) || [];
    });

    // add items for removal via de-selection
    const mappingsToRemove = _differenceWith(this.get(), nextSelections, (model, nextSelection) => model.get('asset_type') === nextSelection.asset_type && model.get('asset_id') === nextSelection.asset_id);
    if (mappingsToRemove.length > 0) {
      this.valueModel.removeMapping(mappingsToRemove);
    }
    this.set(nextSelections);
  }

  /**
   * Puts all the state and selections for each asset type's own collection back at baseline.
   */
  resetAssetCollections() {
    Object.values(AssetType).forEach(assetType => {
      const {
        collection
      } = $assetTags.getAssetCollection(assetType);
      if (collection) {
        collection.resetState();
        collection.clearSelection();
      }
    });
  }
}, _applyDecoratedDescriptor(_class.prototype, "assetTypes", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "assetTypes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "assetTypesHaveFetched", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "assetTypesHaveFetched"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "usage", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "usage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "defaultAssetType", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "defaultAssetType"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectionsByAssetType", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectionsByAssetType"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "initAssetSelections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initAssetSelections"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setMappingsFromAssetSelections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setMappingsFromAssetSelections"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetAssetCollections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetAssetCollections"), _class.prototype), _class);
export default AssetTagValueMappingCollection;